<template>
  <div class="select-none rounded-lg py-4">
      <div v-if="selectedDate" class="absolute pin z-30">
        <div class="fixed pin z-40 bg-black opacity-25" @click="selectedDate = null" />
        <div class="relative flex items-center z-50 w-full h-full">
          <div class="my-10 mx-auto p-6 bg-white rounded-lg shadow-lg overflow-y-scroll w-1/3">
            <div>
              <button
                type="button"
                class="bg-white cursor-pointer float-right"
                @click="selectedDate = null"
              >
                <i class="fas fa-times" />
              </button>

              <strong>{{ selectedDate.date.format('dddd, Do MMMM YYYY') }}</strong>
            </div>
            <div v-for="(event, index) in selectedDate.events" :key="index" class="mt-4 pb-4 border-b leading-tight" >
              <a :href="event.link" target="_blank" class="no-underline text-black flex">
                <div>
                  <h5 class="uppercase text-sm">{{ event.title }}</h5>
                  <p class="text-sm">{{ event.location.display }}</p>
                </div>
                <small class="ml-auto">{{ event.starts.format('h:mmA') }} to {{ event.ends.format('h:mmA') }}</small>
              </a>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-col h-full">
          <div class="flex flex-row mb-2 px-4 text-center">
              <slot name="header">
                  <h3 class="text-white w-full text-base font-bold uppercase">Events Calendar</h3>
              </slot>
          </div>
          <div class="flex flex-row mb-4 px-4">
              <div class="flex-no-grow">
                  <a @click="deincrementMonth" class="text-white cursor-pointer">
                      <slot name="leftArrow">
                        <i class="fas fa-arrow-left"></i>
                      </slot>
                  </a>
              </div>
              <div class="flex-grow text-center">
                  <slot name="monthDisplay" :date="selectedDisplayDate">
                    <h4 class="font-medium font-base text-white text-sm">
                        {{ selectedDisplayDate.format('MMMM YYYY') }}
                    </h4>
                  </slot>
              </div>
              <div class="flex-no-grow">
                  <a @click="incrementMonth" class="text-white cursor-pointer">
                      <slot name="rightArrow">
                          <i class="fas fa-arrow-right"></i>
                      </slot>
                  </a>
              </div>
          </div>
          <div class="flex flex-col h-full">
              <slot name="letterRow">
                  <div class="flex-1 flex flex-row items-center justify-center bg-primary">
                      <div v-for="(dayCharacter, index) in weekDayHeaders" :key="index"
                           class="flex-1">
                          <slot name="headerLetter" :index="index" :character="dayCharacter">
                              <div class="uppercase font-medium text-white text-center text-sm">
                                  {{ dayCharacter }}
                              </div>
                          </slot>
                      </div>
                  </div>
              </slot>
              <div class="flex-1 flex flex-row items-center justify-center"
                   v-for="(week, weekIndex) in currentCalendar" :key="weekIndex">
                  <div class="flex-1 h-10" :class="{ 'cursor-pointer': calendarDate.events.length > 0 }" v-for="(calendarDate, calendarIndex) in week"
                       :key="calendarIndex"
                       @click="selectDate(calendarDate)">
                       <!-- @mouseover="showEventsOverlay(calendarDate.id)" -->
                       <!-- @mouseleave="hideEventsOverlay(calendarDate.id)" -->
                      <slot name="date" :date="calendarDate">
                          <div class="flex items-center justify-center h-10 text-white"
                               :class="calendarClasses(calendarDate)">
                              <div class="text-sm"
                                   :class="{'opacity-25': ! calendarDate.inMonth}">
                                  {{ calendarDate.date.format('D') }}
                              </div>
                          </div>
                      </slot>
                  </div>
              </div>
          </div>
      </div>
  </div>
</template>

<script>
import calendar from '@/mixins/Calendar'
import moment from 'moment'
import _ from 'lodash'
import { mapState } from 'vuex'

export default {
  name: 'Calendar',
  props: {
    month: {
      type: Date,
      default: () => { return moment().startOf('month').toDate() }
    }
  },
  mixins: [calendar],
  data () {
    return {
      selectedMonth: this.month,
      selectedDate: null
    }
  },
  methods: {
    selectDate (date) {
      if (date.events.length > 0) {
        this.selectedDate = date
      }
    },
    showEventsOverlay (ref) {
      if (this.$refs.hasOwnProperty(ref) && this.$refs[ref].length > 0) {
        this.$refs[ref][0].classList.remove('hidden')
        this.$refs[ref][0].classList.add('inline-block')
      }
    },
    hideEventsOverlay (ref) {
      if (this.$refs.hasOwnProperty(ref) && this.$refs[ref].length > 0) {
        this.$refs[ref][0].classList.add('hidden')
        this.$refs[ref][0].classList.remove('inline-block')
      }
    },
    calendarForMonth (date) {
      const month = moment(date)
      const calendar = []

      for (let week = 0; week <= 5; week++) {
        const start = month.clone().add(week, 'week').startOf('week')
        const weekStructure = []

        for (let day = 0; day <= 6; day++) {
          const date = start.clone().add(day, 'day').startOf('day')
          let mappedEvents = _.map(this.eventsForDay(date), (event) => {
            event.starts = moment(event.scheduled_start_at)
            event.ends = moment(event.scheduled_end_at)
            return event
          })
          weekStructure.push({
            id: 'event-' + date.format('YYYYMMDD'),
            date,
            isToday: date.isSame(moment(), 'day'),
            inMonth: date.isSame(month, 'month'),
            events: mappedEvents
          })
        }
        calendar.push(weekStructure)
      }

      return calendar
    },
    eventsForDay (date) {
      return _.filter(this.allEvents, (event) => {
        if ([7, 8].includes(this.selectedOffice.network_id) && event.assigned_countries.length > 0 && event.assigned_countries.includes('UKA')) {
          return (moment(event.scheduled_start_at)
            .isSame(date, 'day') || moment(event.scheduled_end_at).isSame(date, 'day')) && (event.assigned_network === this.selectedOffice.network_id || event.assigned_network == null) && (this.selectedOffice.associate === true || event.assigned_countries.includes(this.selectedOffice.country))
        } else if ([7, 8].includes(this.selectedOffice.network_id) && event.assigned_countries.length > 0) {
          return (moment(event.scheduled_start_at)
            .isSame(date, 'day') || moment(event.scheduled_end_at).isSame(date, 'day')) && (event.assigned_network === this.selectedOffice.network_id || event.assigned_network == null) && (event.assigned_countries.includes(this.selectedOffice.country))
        }
        return (moment(event.scheduled_start_at)
          .isSame(date, 'day') || moment(event.scheduled_end_at).isSame(date, 'day')) && (event.assigned_network === this.selectedOffice.network_id || event.assigned_network == null)
      })
    },
    deincrementMonth () {
      const selected = moment(this.selectedMonth)
        .subtract(1, 'month')
        .startOf('month')

      if (!this.hasEventDataFor(selected)) {
        this.fetchCalendarEvents(selected, selected.clone().endOf('month'))
      }
      this.selectedMonth = selected.toDate()
    },
    incrementMonth () {
      const selected = moment(this.selectedMonth)
        .add(1, 'month')
        .startOf('month')

      if (!this.hasEventDataFor(selected)) {
        this.fetchCalendarEvents(selected, selected.clone().endOf('month'))
      }
      this.selectedMonth = selected.toDate()
    },
    calendarClasses (calendarDate) {
      let calendarClass = {
        'bg-tertiary-darker': calendarDate.isToday && !calendarDate.events.length,
        'bg-secondary font-semibold underline': calendarDate.inMonth && calendarDate.events.length,
        'bg-secondary-darker font-semibold underline': calendarDate.isToday && calendarDate.events.length
      }
      if (this.selectedOffice.network_id === 118) {
        calendarClass = {
          'bg-blue-light font-semibold underline': (calendarDate.isToday && !calendarDate.events.length) || (calendarDate.inMonth && calendarDate.events.length)
        }
      }
      return calendarClass
    }
  },
  computed: {
    ...mapState(['selectedOffice']),
    weekDayHeaders () {
      return ['s', 'm', 't', 'w', 't', 'f', 's']
    },
    allEvents () {
      const events = _.flatten(_.values(this.selectedEvents))
      return _.sortBy(events, ['scheduled_start_at', 'scheduled_end_at'])
    },
    selectedDisplayDate () {
      return moment(this.selectedMonth)
    },
    currentCalendar () {
      return this.calendarForMonth(this.selectedMonth)
    }
  },
  created () {
    this.fetchCalendarEvents(
      moment().startOf('month'),
      moment().add(3, 'month').endOf('month')
    )
  }
}
</script>

<style scoped>
    .events-overlay {
        position: relative;
        align-items: center;
        justify-content: center;
        left: 50%;
        /*transform: translateX(-50%);*/
    }

    .overlay {
        min-width: 180px;
        min-height: 50px;
        border: 1px solid #DDDDDD;
        border-radius: 5px;
        position: absolute;
        top: 1px;
        z-index: 1;
        margin-left: 50%;
        transform: translateX(-50%);
    }

    .overlay::after {
        content: '';
        width: 0;
        height: 0;
        border-right: 10px solid transparent;
        border-left: 10px solid transparent;

        border-bottom: 10px solid #DDDDDD;
        position: absolute;
        top: -10px;
        z-index: 1;
        margin-left: 50%;
        transform: translateX(-50%);
    }

    .overlay::before {
        content: '';
        width: 0;
        height: 0;
        border-right: 10px solid transparent;
        border-left: 10px solid transparent;

        border-bottom: 10px solid white;
        position: absolute;
        top: -9px;
        z-index: 2;
        margin-left: 50%;
        transform: translateX(-50%);
    }
</style>
